import { checkPermissions } from "@elit-hair/elit-tools";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { PERMISSIONS } from "@/constants/permissions";
import useAuth from "@/providers/hooks/useAuth";
import { useGetUsers } from "@/requests/user.queries";
import { UsersStoreFilters, useUsersTableActions, useUsersTableFilters } from "@/stores/use-users-table";
import { User, UserType } from "@/types/user.types";

import UserPanel from "./components/UserPanel";
import UsersTable from "./components/table";

const filterUsers = (users: User[], { isDeleted, userType }: UsersStoreFilters) => {
    if (!isDeleted && !userType) return users;
    return users.filter((user) => {
        return (
            (!isDeleted || !user.isDeleted) &&
            (!userType ||
                user.userType === userType ||
                (userType === UserType.MANAGER &&
                    [UserType.HR, UserType.ADMIN, UserType.MANAGER].includes(user.userType)))
        );
    });
};

function UsersPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { data, isPending, isFetching, refetch } = useGetUsers({ hideManagers: false });
    const api = useGridApiRef();
    const allRows = useMemo(() => data || [], [data]);
    const filters = useUsersTableFilters();
    const { updateFilters } = useUsersTableActions();

    const rows = useMemo(() => {
        return filterUsers(allRows, filters);
    }, [filters, allRows]);

    useEffect(() => {
        const userTypeParam = searchParams.get("userType");
        if (!userTypeParam) return;

        const userType = Number(userTypeParam);
        if (Object.values(UserType).includes(userType) && filters.userType !== userType) {
            updateFilters({ userType });
        }

        const next = new URLSearchParams(searchParams);
        next.delete("userType");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, updateFilters, filters.userType]);

    const { userData } = useAuth();
    const pagePermissions = useMemo(
        () => ({
            edit: checkPermissions(userData?.permissionCodes || [], [PERMISSIONS.TR_SVC_HR_MANAGE_USERS]),
        }),
        [userData]
    );

    return (
        <UserPanel pagePermissions={pagePermissions}>
            <UsersTable
                apiRef={api}
                loading={isPending || isFetching}
                rows={rows}
                allRows={allRows}
                refetch={refetch}
                pagePermissions={pagePermissions}
            />
        </UserPanel>
    );
}

export default UsersPage;
